
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  FileText, 
  Settings, 
  Users, 
  MessageSquare,
  Bot,
  LogOut,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface SidebarItemProps {
  icon: React.ElementType;
  label: string;
  href: string;
  active?: boolean;
}

const SidebarItem = ({ icon: Icon, label, href, active }: SidebarItemProps) => (
  <Link
    to={href}
    className={cn(
      "flex items-center py-2 px-3 text-sm rounded-md transition-colors",
      active 
        ? "bg-primary/10 text-primary font-medium" 
        : "text-muted-foreground hover:text-foreground hover:bg-muted"
    )}
  >
    <Icon className="h-4 w-4 mr-2 flex-shrink-0" />
    <span>{label}</span>
  </Link>
);

export const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  // Get admin info from localStorage
  const getAdminInfo = () => {
    const adminAuth = localStorage.getItem('adminAuth');
    return adminAuth ? JSON.parse(adminAuth) : null;
  };
  
  const adminInfo = getAdminInfo();
  
  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const isActive = (path: string) => {
    return pathname === path;
  };

  return (
    <div className="border-r border-border h-screen w-64 flex-shrink-0 hidden md:block overflow-y-auto">
      <div className="p-4">
        <h2 className="text-xl font-bold mb-1">Admin Panel</h2>
        <p className="text-sm text-muted-foreground mb-6">Manage your site</p>
        
        {/* Admin info */}
        {adminInfo && (
          <div className="mb-6 p-4 bg-muted/50 rounded-md">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mr-2">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">{adminInfo.email}</p>
                <p className="text-xs text-muted-foreground">Administrator</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2 text-sm"
              onClick={handleLogout}
            >
              <LogOut className="h-3 w-3 mr-1" />
              Logout
            </Button>
          </div>
        )}
        
        <nav className="space-y-1">
          <SidebarItem 
            icon={LayoutDashboard} 
            label="Admin - Dashboard" 
            href="/admin-dashboard" 
            active={isActive('/admin-dashboard')} 
          />
          <SidebarItem 
            icon={FileText} 
            label="Admin - All Reports" 
            href="/admin/reports" 
            active={isActive('/admin/reports')} 
          />
          <SidebarItem 
            icon={Settings} 
            label="Admin - Test Pipeline" 
            href="/admin/test-pipeline" 
            active={isActive('/admin/test-pipeline')} 
          />
          <SidebarItem 
            icon={FileText} 
            label="Admin - Blog Management" 
            href="/blog-admin" 
            active={isActive('/blog-admin')} 
          />
          <SidebarItem 
            icon={Bot} 
            label="Admin - AI Settings" 
            href="/admin/ai-settings" 
            active={isActive('/admin/ai-settings')} 
          />
          <SidebarItem 
            icon={MessageSquare} 
            label="Admin - Conversation Data" 
            href="/conversation-data" 
            active={isActive('/conversation-data')} 
          />
          <SidebarItem 
            icon={Users} 
            label="Admin - User Management" 
            href="/user-management" 
            active={isActive('/user-management')} 
          />
        </nav>
      </div>
    </div>
  );
};

export default AdminSidebar;
